import { useState, useEffect, useMemo, useRef } from 'react';
import type { Event } from '@buzzmap/shared';
import { useDiscoveryOptional, buildDiscoveryRequestHeaders } from '../contexts/discoveryStore';

interface UseWeekendNearbyOptions {
  /**
   * ALL resolved search-area centers (T012-FE). Takes precedence over
   * `coords`: a region gallery must query every center, never only the first
   * (plan l.64). Ignored when empty.
   */
  centers?: Array<{ lat: number; lng: number }>;
  /** Legacy single-center input (pre-T012 callers and the legacy window). */
  coords?: [number, number];
  radiusKm?: number;
  limit?: number;
  /**
   * Explicit category filter from the URL (T012-FE, plan l.64): the weekend
   * gallery shows only the selected categories when one is active. Empty =
   * no category constraint (legacy behaviour).
   */
  categories?: string[];
  /**
   * Explicit date constraint from the URL (T012-FE, plan l.64): intersected
   * with the weekend window — a chosen range that misses the weekend yields
   * an empty gallery, never out-of-filter events.
   */
  startDate?: string;
  endDate?: string;
  /**
   * When true, hold the fetch entirely (no request fires, `loading` stays
   * true). Same territory-route gate as `useTopRankedEvents` — see
   * `UserContext.locationResolutionPending`. Defaults to false.
   */
  skip?: boolean;
}

interface UseWeekendNearbyResult {
  events: Event[];
  loading: boolean;
  error: string | null;
  weekendStart: string;
  weekendEnd: string;
}

/**
 * Compute today's date in Europe/Rome as 'YYYY-MM-DD' (locale en-CA gives ISO-like format).
 */
function todayInRome(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });
}

/** Get day-of-week (0 = Sunday, 6 = Saturday) for a YYYY-MM-DD string. */
function dayOfWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
}

function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const ms = Date.UTC(y!, m! - 1, d!) + days * 86400000;
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Compute the upcoming weekend window relative to `today` (YYYY-MM-DD, Europe/Rome).
 * - Mon-Fri (1-5): from = next Saturday, to = next Sunday
 * - Sat (6):       from = today, to = tomorrow (Sun)
 * - Sun (0):       from = today, to = today
 */
export function computeWeekendRange(today: string): { from: string; to: string } {
  const dow = dayOfWeek(today);
  if (dow === 0) return { from: today, to: today };
  if (dow === 6) return { from: today, to: addDays(today, 1) };
  // Monday(1)..Friday(5) -> days until Saturday
  const daysUntilSat = 6 - dow;
  const from = addDays(today, daysUntilSat);
  const to = addDays(from, 1);
  return { from, to };
}

/**
 * Fetch events occurring on the upcoming weekend, optionally filtered by
 * centers + radius. If no center is given, falls back to nationwide
 * popularity-soon ordering.
 *
 * T012-FE: accepts ALL resolved centers, intersects explicit URL dates with
 * the weekend, applies the explicit category selection, and declares the
 * discovery epoch headers (plan l.64).
 */
export function useWeekendNearbyEvents(opts: UseWeekendNearbyOptions): UseWeekendNearbyResult {
  const {
    centers,
    coords,
    radiusKm = 75,
    limit = 10,
    categories,
    startDate,
    endDate,
    skip = false,
  } = opts;

  const discovery = useDiscoveryOptional();
  // T018-FE (T018 matrix finding 1): gate on the RESOLVED epoch — the same
  // `mode` signal `EventsContext` (W-D) and `useMapEvents` gate on. The fetch
  // effect below holds while the bootstrap is in flight, so a cold load never
  // fires a headerless pre-bootstrap probe (which the ACTIVATED server
  // answers 426; the old "empty object pre-bootstrap is a pass-through" claim
  // was only true before activation). `null` (no `DiscoveryProvider`: organizer
  // host, isolated tests) fetches as today.
  const mode = discovery?.mode ?? null;
  // S-1 (T012 review): ref writes belong after commit, not in the render
  // body; the ref (not the headers value) keeps a capabilities arrival from
  // re-keying the fetch effect. Declared before the fetch effect so the
  // first request already reads the populated ref.
  const headersRef = useRef<Record<string, string>>({});
  useEffect(() => {
    // T018-FE: the epoch metadata is a v2 declaration. Legacy fetches stay
    // byte-identical to HEAD (no headers); in `v2` the capabilities are always
    // known (the gate cannot resolve to v2 without them), so the very first
    // request of a v2 session is already pinned.
    headersRef.current =
      mode === 'v2' ? buildDiscoveryRequestHeaders(discovery?.capabilities ?? null) : {};
  });

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const today = todayInRome();
  const { from: weekendStart, to: weekendEnd } = computeWeekendRange(today);

  const effectiveCenters = useMemo(() => {
    if (centers && centers.length > 0) return centers;
    if (coords) return [{ lat: coords[0], lng: coords[1] }];
    return undefined;
  }, [centers, coords]);

  const centersKey = effectiveCenters ? JSON.stringify(effectiveCenters) : '';
  const categoriesKey = categories && categories.length > 0 ? [...categories].sort().join(',') : '';
  const startDateKey = startDate ?? '';
  const endDateKey = endDate ?? '';

  useEffect(() => {
    // T018-FE: the epoch gate, same shape as EventsContext's W-D branches.
    // - `upgrade-required`: the blocking state owns the session — never fetch.
    // - `loading`: hold until the capabilities bootstrap resolves, so the
    //   session's FIRST gallery request is the negotiated one (v2) or the
    //   HEAD-shaped legacy one — never a headerless pre-bootstrap probe that
    //   426s at an activated epoch. Pre-activation cost: one capabilities RTT,
    //   the same documented delta as EventsContext/useTerritoryRouteResolution.
    if (mode === 'upgrade-required') {
      setLoading(false);
      return;
    }
    if (mode === 'loading') {
      setLoading(true);
      return;
    }

    if (skip) {
      setLoading(true);
      return;
    }

    // Weekend ∩ explicit URL dates. Disjoint → honest empty, never events
    // outside the user's chosen range (plan l.64).
    const from = startDate && startDate > weekendStart ? startDate : weekendStart;
    const to = endDate && endDate < weekendEnd ? endDate : weekendEnd;
    if (from > to) {
      setEvents([]);
      setError(null);
      setLoading(false);
      return;
    }

    const parsedCenters: Array<{ lat: number; lng: number }> | undefined = centersKey
      ? (JSON.parse(centersKey) as Array<{ lat: number; lng: number }>)
      : undefined;

    const controller = new AbortController();

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set('orderBy', 'popularity-soon');
        params.set('startDate', from);
        params.set('endDate', to);
        params.set('pageSize', String(limit));
        params.set('day', today);
        if (categoriesKey) params.set('category', categoriesKey);

        if (parsedCenters) {
          params.set('centers', JSON.stringify(parsedCenters));
          params.set('radiusKm', String(radiusKm));
        }

        const res = await fetch(`/api/events?${params.toString()}`, {
          signal: controller.signal,
          // Epoch metadata (T010 W6 / T018-FE): pinned in v2 (the activated
          // server 426s unversioned discovery requests); `{}` in legacy, which
          // only exists while the server is NOT activated (pass-through gate).
          // The fetch effect never runs before the mode resolves, so `{}` can
          // no longer mean a pre-bootstrap probe against an activated epoch.
          headers: headersRef.current,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list: Event[] = data.events ?? [];
        setEvents(list.slice(0, limit));
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'weekend-nearby error');
      } finally {
        // T018 review W-2 (same guard as EventsContext): on a mid-flight
        // abort (mode/key change) the rejection lands AFTER the next effect
        // run's `setLoading(true)` — clearing it here would read `loading`
        // false while the replacement fetch is still in flight. On abort
        // there is always a following effect run (which owns the state) or an
        // unmount.
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => controller.abort();
    // `mode` IS a dependency (T018-FE): the held `loading → legacy/v2`
    // transition is exactly when the cold-start fetch must fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centersKey,
    radiusKm,
    limit,
    categoriesKey,
    startDateKey,
    endDateKey,
    weekendStart,
    weekendEnd,
    mode,
    skip,
  ]);

  return { events, loading, error, weekendStart, weekendEnd };
}
